import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Loader2 } from 'lucide-react'
import { Input } from '@components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { listAdasCategories } from '../../../api/scrubApi'
import { Logger } from '../../../logger'

const CAL_TYPES = ['Static', 'Dynamic']

const REASONS = ['VIN Decode', 'Estimate', 'False Positive', 'Missed']

export const AddCalibrationForm = ({ onCancel, onSubmit, isSubmitting }) => {
  const [displayTitle, setDisplayTitle] = useState('')
  const [adasCategory, setAdasCategory] = useState('')
  const [calibrationMethod, setCalibrationMethod] = useState('')
  const [reason, setReason] = useState('')
  const [categories, setCategories] = useState([])
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)

  useEffect(() => {
    let cancelled = false
    setIsLoadingCategories(true)
    listAdasCategories()
      .then((list) => {
        if (cancelled) return
        setCategories(Array.isArray(list) ? list : [])
      })
      .catch((error) => {
        Logger.error({ message: error, payload: { file: 'AddCalibrationForm', method: 'listAdasCategories' } })
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCategories(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const categoryPlaceholder = isLoadingCategories ? 'Loading…' : categories.length === 0 ? 'No categories available' : 'Category'

  const canSubmit = displayTitle.trim() && adasCategory && calibrationMethod && !isSubmitting

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit({
      displayTitle: displayTitle.trim(),
      adasCategory,
      calibrationMethod,
      reason: reason || ''
    })
  }

  return (
    <div className='rounded-xl border border-dashed border-[#1F6FEB] bg-white p-4'>
      <Input
        value={displayTitle}
        onChange={(e) => setDisplayTitle(e.target.value)}
        placeholder='Type calibration name...'
        className='h-10'
      />

      <div className='mt-3 grid grid-cols-3 gap-2'>
        <Select value={adasCategory} onValueChange={setAdasCategory} disabled={isLoadingCategories || categories.length === 0}>
          <SelectTrigger className='h-9 rounded-lg'>
            <SelectValue placeholder={categoryPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={calibrationMethod} onValueChange={setCalibrationMethod}>
          <SelectTrigger className='h-9 rounded-lg'>
            <SelectValue placeholder='Type' />
          </SelectTrigger>
          <SelectContent>
            {CAL_TYPES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger className='h-9 rounded-lg'>
            <SelectValue placeholder='Reason' />
          </SelectTrigger>
          <SelectContent>
            {REASONS.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className='mt-3 flex justify-end gap-2'>
        <button
          type='button'
          onClick={onCancel}
          disabled={isSubmitting}
          className='h-8 rounded-lg px-3 text-sm text-[#6B7280] hover:bg-[#F8FAFC] disabled:opacity-60'
        >
          Cancel
        </button>
        <button
          type='button'
          onClick={handleSubmit}
          disabled={!canSubmit}
          className='inline-flex h-8 items-center gap-2 rounded-lg bg-[#0A1628] px-3 text-sm font-semibold text-white hover:opacity-90 disabled:pointer-events-none disabled:opacity-60'
        >
          {isSubmitting && <Loader2 className='h-3.5 w-3.5 animate-spin' />}
          Add
        </button>
      </div>
    </div>
  )
}

AddCalibrationForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool
}
